import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// How an org owner or admin gets inside a Boo they don't own from the Boo
// itself (the org Access page's handover is the other way). Never quiet: the
// reason goes to the Boo's owners and admins and stays in its history.
export function TakeOwnershipDialog({
  containerId,
  booName,
  open,
  onOpenChange,
}: {
  containerId: string;
  booName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  const take = useMutation({
    mutationFn: () => api.containers.members.assignOwner(containerId, { reason: trimmed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      queryClient.invalidateQueries({ queryKey: ["boo-members", containerId] });
      queryClient.invalidateQueries({ queryKey: ["boo-access-events", containerId] });
      toast.success(`You're now an owner of ${booName}`);
      setReason("");
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Take ownership of {booName}?</DialogTitle>
          <DialogDescription>
            You&apos;ll be able to see and change everything in {booName}. We&apos;ll email its owners and admins
            your reason, and they&apos;ll see a notice in the Boo for the next two weeks.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="take-ownership-reason">Reason</Label>
          <Textarea
            id="take-ownership-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="The previous owner left and the team still relies on this Boo."
            maxLength={500}
            rows={3}
          />
        </div>
        {take.isError && <InlineError error={take.error} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={trimmed.length < 3 || take.isPending} onClick={() => take.mutate()}>
            {take.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Take ownership
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
